import React, { useRef, useEffect, useState } from 'react';

interface FlowChartItem {
  name: string;
  category: string;
  cnt: string;
}

const EDFlowChart: React.FC<{data: FlowChartItem[]}> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: Math.max(400, window.innerHeight * 0.5)
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const getCount = (name: string) => {
    if (!Array.isArray(data)) return '-';
    const item = data.find(item => item.name === name);
    return item ? item.cnt : '0';
  };

  const getWaitingRoomCount = () => {
    if (!Array.isArray(data)) return '-';
    const arrived = parseInt(getCount('arrived')) || 0;
    const roomed = parseInt(getCount('roomed')) || 0;
    return Math.max(0, arrived - roomed).toString();
  };

  const getBoardingCount = () => {
    if (!Array.isArray(data)) return '-';
    const totalBedRequests = data
      .filter(item => item.name === 'bed_requests')
      .reduce((sum, item) => sum + (parseInt(item.cnt) || 0), 0);
    const departedAdmit = data
      .filter(item => item.name === 'departed' && item.category !== 'Discharge')
      .reduce((sum, item) => sum + (parseInt(item.cnt) || 0), 0);
    return Math.max(0, totalBedRequests - departedAdmit).toString();
  };

  const getAdmittedCount = () => {
    if (!Array.isArray(data)) return '-';
    const departedAdmit = data
      .filter(item => item.name === 'departed' && item.category !== 'Discharge')
      .reduce((sum, item) => sum + (parseInt(item.cnt) || 0), 0);
    return departedAdmit.toString();
  };

  const getDischargeCount = () => {
    if (!Array.isArray(data)) return '-';
    return data
      .filter(item => item.name === 'departed' && item.category === 'Discharge')
      .reduce((sum, item) => sum + (parseInt(item.cnt) || 0), 0)
      .toString();
  };

  const baseWidth = 1000;
  const scale = dimensions.width / baseWidth;
  const PATH_OFFSET = 70 * scale;
  const verticalSpacing = 100;
  const centerY = 150;

  const positions = {
    arrival: { x: 100 * scale, y: centerY },
    waiting: { x: 300 * scale, y: centerY },
    roomed: { x: 500 * scale, y: centerY },
    admitted: { x: 800 * scale, y: centerY - verticalSpacing },
    boarding: { x: 800 * scale, y: centerY },
    departed: { x: 500 * scale, y: centerY + verticalSpacing }
  };

  return (
    <div ref={containerRef} className="w-full">
      <svg 
        width="100%" 
        height={dimensions.height}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
          </marker>
        </defs>

        <g>
          {/* Boxes */}
          {[
            { ...positions.arrival, text: "Arrival", count: getCount('arrived') },
            { ...positions.waiting, text: "Waiting Room", count: getWaitingRoomCount() },
            { ...positions.roomed, text: "Roomed", count: getCount('roomed') },
            { ...positions.admitted, text: "Admitted", count: getAdmittedCount() },
            { ...positions.boarding, text: "Boarding", count: getBoardingCount() },
            { ...positions.departed, text: "Departed", count: getDischargeCount() }
          ].map(({ x, y, text, count }, i) => (
            <g key={i} transform={`translate(${x},${y})`}>
              <rect
                x={-70 * scale}
                y={-30 * scale}
                width={140 * scale}
                height={60 * scale}
                rx={5 * scale}
                fill="white"
                stroke="#666"
                strokeWidth={1 * scale}
              />
              <text
                textAnchor="middle"
                fontSize={14 * scale}
                fill="#333"
              >
                <tspan x="0" y={-8 * scale}>{text}</tspan>
                <tspan x="0" y={16 * scale}>{count}</tspan>
              </text>
            </g>
          ))}

          {/* Horizontal arrows */}
          {[
            { start: positions.arrival, end: positions.waiting },
            { start: positions.waiting, end: positions.roomed },
            { start: positions.roomed, end: positions.boarding, label: "Bed Request" },
          ].map(({ start, end, label }, i) => (
            <g key={i}>
              <line
                x1={start.x + PATH_OFFSET}
                y1={start.y}
                x2={end.x - PATH_OFFSET}
                y2={end.y}
                stroke="#666"
                strokeWidth={1 * scale}
                markerEnd="url(#arrowhead)"
              />
              {label && (
                <text
                  x={(start.x + end.x) / 2}
                  y={(start.y + end.y) / 2 - 10 * scale}
                  textAnchor="middle"
                  fontSize={12 * scale}
                  fill="#666"
                >
                  {label}
                </text>
              )}
            </g>
          ))}

          {/* Vertical arrows and paths */}
          <g>
            {/* Roomed to Departed (vertical) */}
            <line
              x1={positions.roomed.x}
              y1={positions.roomed.y + 30 * scale}
              x2={positions.departed.x}
              y2={positions.departed.y - 30 * scale}
              stroke="#666"
              strokeWidth={1 * scale}
              markerEnd="url(#arrowhead)"
            />
            <text
              x={positions.roomed.x + 20 * scale}
              y={(positions.roomed.y + positions.departed.y) / 2}
              textAnchor="start"
              fontSize={12 * scale}
              fill="#666"
            >
              Discharge
            </text>

            {/* Boarding to Admitted (vertical) */}
            <line
              x1={positions.boarding.x}
              y1={positions.boarding.y - 30 * scale}
              x2={positions.admitted.x}
              y2={positions.admitted.y + 30 * scale}
              stroke="#666"
              strokeWidth={1 * scale}
              markerEnd="url(#arrowhead)"
            />

            {/* Roomed to Admitted (elbow) */}
            <path
              d={`M ${positions.roomed.x} ${positions.roomed.y - 30 * scale} 
                  L ${positions.roomed.x} ${positions.admitted.y} 
                  L ${positions.admitted.x - 70 * scale} ${positions.admitted.y}`}
              fill="none"
              stroke="#666"
              strokeWidth={1 * scale}
              markerEnd="url(#arrowhead)"
            />
            <text
              x={positions.roomed.x}
              y={positions.admitted.y - 15 * scale}
              textAnchor="start"
              fontSize={12 * scale}
              fill="#666"
            >
              Bed Request
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default EDFlowChart;